import { useMemo } from "react";
import { Collapsible, PresenceMark, RoleLabel } from "./CompareBits";
import {
  ADDED_BY_EVAL,
  DROPPED_BY_EVAL,
  buildDocComparison,
  describeDocComparison,
} from "./docDiff";
import { formatDocClassification, unwrapStageOutput } from "./evalOutput";

const STATUS_LABELS = {
  [DROPPED_BY_EVAL]: "Dropped by Eval",
  [ADDED_BY_EVAL]: "Added by Eval",
};

const isPreviewable = (mimeType) => {
  const mime = String(mimeType || "").toLowerCase();

  return (
    mime.includes("pdf") || mime.startsWith("image/") || mime.includes("text/")
  );
};

function readSelectedIds(output) {
  const ids = unwrapStageOutput(output).json?.selectedDocumentIds;

  return Array.isArray(ids) ? ids.map(String) : [];
}

function DocName({ row }) {
  const previewUrl = row.doc?.previewUrl || null;
  const classification = formatDocClassification(
    row.doc?.classification ?? row.doc?.category,
  );

  return (
    <div className="mse-doc-row__main">
      {previewUrl ? (
        <a
          href={previewUrl}
          target="_blank"
          rel="noreferrer"
          className="mse-doc-row__name"
        >
          {row.name}
          {!isPreviewable(row.doc?.mimeType) ? " ↗" : ""}
        </a>
      ) : (
        <span className="mse-doc-row__name">{row.name}</span>
      )}
      {classification ? (
        <span className="mse-doc-row__meta">{classification}</span>
      ) : null}
      <code className="mse-mono mse-doc-row__id">{row.id}</code>
    </div>
  );
}

export default function DocSelectionDiff({
  caseRow,
  docsById = {},
  docsLoading,
  baseModel,
  evalModel,
}) {
  const structured = caseRow?.diff?.structured;
  const baselineIds = useMemo(
    () => readSelectedIds(caseRow?.baselineOutputText),
    [caseRow?.baselineOutputText],
  );
  const candidateIds = useMemo(
    () => readSelectedIds(caseRow?.candidate?.candidateOutputText),
    [caseRow?.candidate?.candidateOutputText],
  );

  // The report embeds the documents each pipeline saw; merge them over the
  // loan-wide lookup so names resolve even for documents since deleted.
  const resolvedDocsById = useMemo(() => {
    const merged = { ...docsById };
    const collect = (output) => {
      const docs = unwrapStageOutput(output).json?.selectedDocuments;
      if (!Array.isArray(docs)) {
        return;
      }
      docs.forEach((doc) => {
        if (!doc?.id) {
          return;
        }
        merged[String(doc.id)] = { ...merged[String(doc.id)], ...doc };
      });
    };
    collect(caseRow?.baselineOutputText);
    collect(caseRow?.candidate?.candidateOutputText);

    return merged;
  }, [
    caseRow?.baselineOutputText,
    caseRow?.candidate?.candidateOutputText,
    docsById,
  ]);

  const { rows, counts, overlap } = useMemo(
    () =>
      buildDocComparison({
        baselineIds,
        candidateIds,
        docsById: resolvedDocsById,
      }),
    [baselineIds, candidateIds, resolvedDocsById],
  );

  const overlapPct =
    structured?.selectedDocumentIds?.jaccard ?? overlap ?? null;
  const hasRegression = counts.dropped > 0;

  return (
    <div className="mse-doc-diff">
      <div
        className={`mse-diffsummary ${
          hasRegression ? "mse-diffsummary--bad" : "mse-diffsummary--good"
        }`}
      >
        <strong>{describeDocComparison(counts)}</strong>
        <span className="mse-diffsummary__meta">
          Base selected {counts.base} · Eval selected {counts.eval} ·{" "}
          {overlapPct == null
            ? "overlap —"
            : `${(Number(overlapPct) * 100).toFixed(0)}% overlap`}
        </span>
      </div>

      {docsLoading && (
        <p className="mse-muted">Resolving document names…</p>
      )}

      <div className="mse-table-scroll">
        <table className="mse-table mse-table--compare">
          <thead>
            <tr>
              <th>Document</th>
              <th className="mse-col-mark">
                <RoleLabel role="base" model={baseModel} />
              </th>
              <th className="mse-col-mark">
                <RoleLabel role="eval" model={evalModel} />
              </th>
              <th>Difference</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={`mse-cmprow mse-cmprow--${row.status}`}>
                <td>
                  <DocName row={row} />
                </td>
                <td className="mse-col-mark">
                  <PresenceMark present={row.isInBase} />
                </td>
                <td className="mse-col-mark">
                  <PresenceMark
                    present={row.isInEval}
                    isRegression={row.status === DROPPED_BY_EVAL}
                  />
                </td>
                <td>
                  {STATUS_LABELS[row.status] ? (
                    <span className={`mse-diffbadge mse-diffbadge--${row.status}`}>
                      {STATUS_LABELS[row.status]}
                    </span>
                  ) : (
                    <span className="mse-muted-xs">match</span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="mse-muted">
                  Neither pipeline selected any documents.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Collapsible title="Why each pipeline picked these documents">
        <div className="mse-side-by-side">
          <div>
            <h4>
              <RoleLabel role="base" model={baseModel} />
            </h4>
            <pre className="mse-pre">
              {structured?.baselineReasoning ||
                unwrapStageOutput(caseRow?.baselineOutputText).json?.reasoning ||
                "—"}
            </pre>
          </div>
          <div>
            <h4>
              <RoleLabel role="eval" model={evalModel} />
            </h4>
            <pre className="mse-pre">
              {structured?.candidateReasoning ||
                unwrapStageOutput(caseRow?.candidate?.candidateOutputText).json
                  ?.reasoning ||
                "—"}
            </pre>
          </div>
        </div>
      </Collapsible>
    </div>
  );
}
